import type { CatalogFieldValues } from "@/lib/catalogRecordDisplay";
import type { MarkingRecord } from "@/services/markings";
import { ColorText } from "@/components/ColorText";

/**
 * Fixed catalog fields for Catalog Search and Record Detail.
 * Omits Shape and Dimensions for Manuscripts; other missing values use the empty marker
 * (see `buildCatalogFieldValues`). Search list and gallery cards use
 * different, type-specific field sets. Dates render in a dedicated bottom
 * row so Earliest/Latest stay side-by-side.
 */
type CatalogRecordField = {
  label: string;
  value: string;
};

function hasValue(value: string): boolean {
  return value.trim() !== "" && value !== "-";
}

function townmarkDescriptor(row: CatalogFieldValues): CatalogRecordField {
  if (hasValue(row.lettering)) {
    return { label: "Lettering Style", value: row.lettering };
  }
  if (hasValue(row.impression)) {
    return { label: "Impression", value: row.impression };
  }
  if (hasValue(row.irregular)) {
    return { label: "Irregular", value: row.irregular };
  }
  return { label: "Lettering Style", value: row.lettering };
}

function searchFields(
  row: CatalogFieldValues,
  variant: "list" | "gallery",
): CatalogRecordField[] {
  const type = { label: "Type", value: row.type };
  const manuscript = { label: "Manuscript", value: row.manuscript };
  const dimensions = { label: "Dimensions", value: row.dimensions };
  const color = { label: "Color", value: row.color };
  const isRatemark = row.type.trim().toLowerCase() === "ratemark";

  if (variant === "gallery") {
    return isRatemark
      ? [
          type,
          manuscript,
          dimensions,
          { label: "Rate Value", value: row.rateValue },
        ]
      : [type, manuscript, dimensions, color];
  }

  return isRatemark
    ? [
        type,
        manuscript,
        { label: "Shape", value: row.shape },
        { label: "Rate Value", value: row.rateValue },
        dimensions,
        color,
      ]
    : [
        type,
        manuscript,
        { label: "Shape", value: row.shape },
        townmarkDescriptor(row),
        dimensions,
        color,
      ];
}

function contributionFields(row: CatalogFieldValues): CatalogRecordField[] {
  const fields: CatalogRecordField[] = [
    { label: "Type", value: row.type },
    { label: "Manuscript", value: row.manuscript },
  ];
  // T70: only when the submission names one; the header already shows town and state.
  if (row.county && hasValue(row.county)) fields.push({ label: "County", value: row.county });
  const isManuscript = row.manuscript.trim().toLowerCase() === "yes";
  const type = row.type.trim().toLowerCase();
  const isRatemark = type === "ratemark";
  const isAuxmark = type === "auxmark";

  // The description is rendered after the dates, in full -- where the form
  // put it (issues.md 173). Only a struck townmark carries the device fields.
  if (!isManuscript && type === "townmark") {
    fields.push(
      { label: "Shape", value: row.shape },
      { label: "Lettering style", value: row.lettering },
      { label: "Dimensions", value: row.dimensions },
    );
  }

  fields.push({ label: "Color", value: row.color });
  if (isRatemark) {
    fields.push({ label: "Rate Value", value: row.rateValue });
  } else if (isAuxmark) {
    fields.push({ label: "Shape", value: row.shape });
  }
  return fields;
}

export function CatalogRecordFields({
  row,
  variant = "list",
}: {
  row: CatalogFieldValues;
  record?: MarkingRecord;
  variant?: "list" | "gallery" | "detail" | "contribution";
}) {
  const reviewDescription =
    variant === "contribution" && row.desc.trim() && row.desc.trim() !== "-" ? row.desc : "";
  const fields =
    variant === "detail"
      ? [
          { label: "Type", value: row.type },
          { label: "Manuscript", value: row.manuscript },
          { label: "Shape", value: row.shape },
          { label: "Lettering style", value: row.lettering },
          { label: "Dimensions", value: row.dimensions },
          { label: "Color", value: row.color },
        ]
      : variant === "contribution"
        ? contributionFields(row)
        : searchFields(row, variant);
  const visibleFields = variant !== "contribution" && row.manuscript.trim().toLowerCase() === "yes"
    ? fields.filter((field) => field.label !== "Shape" && field.label !== "Dimensions")
    : fields;

  return (
    <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
      {visibleFields.map((field) => (
        <div key={field.label} className="min-w-0">
          <span className="text-muted-foreground">{field.label}:</span>{" "}
          <span className="text-foreground break-words">
            {field.label === "Color" ? (
              <ColorText name={field.value} hex={row.colorHex} />
            ) : field.value}
          </span>
        </div>
      ))}
      <div className="min-w-0 sm:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2">
        <div className="min-w-0">
          <span className="text-muted-foreground">Earliest Seen:</span>{" "}
          <span className="text-foreground break-words">{row.earliestSeen}</span>
        </div>
        <div className="min-w-0">
          <span className="text-muted-foreground">Latest Seen:</span>{" "}
          <span className="text-foreground break-words">{row.latestSeen}</span>
        </div>
      </div>
      {reviewDescription && (
        <div className="min-w-0 sm:col-span-2">
          <span className="text-muted-foreground">Description:</span>{" "}
          <span className="text-foreground break-words whitespace-pre-line">{reviewDescription}</span>
        </div>
      )}
    </dl>
  );
}
